/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Nikita Tropin <ntropin@yandex.ru>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnInit} from '@angular/core';
import {Assignee, BaseComponent, Board, FileService, IdAndHeader, Picture, StoreService} from 'compob2b-libs';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {filter, takeUntil, tap} from 'rxjs/operators';
import {FormControl, FormGroup} from '@angular/forms';
import {TuiDay, TuiDayRange} from '@taiga-ui/cdk';
import {getDate, getMonth, getYear} from 'date-fns';

@Component({
    selector: 'app-filter-issue',
    templateUrl: './filter-issue.component.html',
    styleUrls: ['./filter-issue.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FilterIssueComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() board: Board;
  @Input() projectId: number;

  inputFormControl: FormControl = new FormControl();

  private emptyFliter: string = 'Не назначено';

  assigneeList: Assignee[];
  currentAssignee: Assignee;
  private assigneeMap: Map<string, Assignee> = new Map<string, Assignee>();
  protected selectedAssignee: Assignee;

  paymentList: IdAndHeader[];
  private paymentMap: Map<number, IdAndHeader> = new Map<number, IdAndHeader>();
  selectedPayment: IdAndHeader;

  epicList: string[] = [];
  tagList: string[] = [];
  environmentList: string[] = [];
  selectedEpic: string;
  selectedTag: string;
  selectedEnvironment: string;
  rangeForm: FormGroup;
  releasedForm: FormGroup;
  userId: number;
  myAssignee: boolean = false;

  constructor(
    private fileService: FileService,
    private readonly router: Router,
    protected readonly route: ActivatedRoute,
    protected readonly storeService: StoreService) {
    super();

    this.userId = this.storeService.getCustomer().id;

    this.rangeForm = new FormGroup({
      dateRange: new FormControl()
    });
    this.rangeForm.get('dateRange').valueChanges
      .pipe(
        tap((value) => this.filterDate()),
        takeUntil(this.unsubscribe)
      ).subscribe();

    this.releasedForm = new FormGroup({
      releasedRange: new FormControl()
    });
    this.releasedForm.get('releasedRange').valueChanges
      .pipe(
        tap((value) => this.filterReleased()),
        takeUntil(this.unsubscribe)
      ).subscribe();
  }

  ngOnChanges(): void {
    this.initFilters();
  }

  ngOnInit(): void {
    this.inputFormControl.valueChanges
      .pipe(
        filter((term: string) => term !== null),
        tap((term: string) => this.filterName(term)),
        takeUntil(this.unsubscribe)
      ).subscribe();

    this.route.queryParams
      .pipe(
        tap((params: Params) => this.setFilteredParams(params)),
        takeUntil(this.unsubscribe)
      ).subscribe();
  }

  private setFilteredParams(params: Params): void {
    if (this.assigneeMap.has(params.assignee)) {
      this.selectedAssignee = this.assigneeMap.get(params.assignee);
      this.myAssignee = true;
    }

    if (params.payment && this.paymentMap.has(parseInt(params.payment, 10))) {
      this.selectedPayment = this.paymentMap.get(parseInt(params.payment, 10));
    }

    if (params.from && params.to) {
      const from = new Date(params.from);
      const to = new Date(params.to);

      const range = new TuiDayRange(
        new TuiDay(getYear(from), getMonth(from), getDate(from)),
        new TuiDay(getYear(to), getMonth(to), getDate(to)),
      );

      this.rangeForm.get('dateRange').setValue(range);
    }

    if (params.rfrom && params.rto) {
      const from = new Date(params.rfrom);
      const to = new Date(params.rto);

      const range = new TuiDayRange(
        new TuiDay(getYear(from), getMonth(from), getDate(from)),
        new TuiDay(getYear(to), getMonth(to), getDate(to)),
      );

      this.releasedForm.get('releasedRange').setValue(range);
    }

    if (params.term) {
      this.inputFormControl.setValue(params.term);
    }

    if (params.epic && this.epicList.indexOf(params.epic) >= 0) {
      this.selectedEpic = params.epic;
    }

    if (params.tag && this.tagList.indexOf(params.tag) >= 0) {
      this.selectedTag = params.tag;
    }
    if (params.environment && this.environmentList.indexOf(params.environment) >= 0) {
      this.selectedEnvironment = params.environment;
    }
  }

  private initFilters(): void {
    this.assigneeMap.clear();
    this.paymentMap.clear();
    this.epicList = [];
    this.tagList = [];
    this.environmentList = [];

    let showNoAssignee = false;
    let showNoPayment = false;
    let showNoEpic = false;
    this.board.columns.forEach(column => {
      column.tasks.forEach(task => {
        if (task.assignee !== null) {
          if (!this.assigneeMap.has(task.assignee.uid)) {
            this.assigneeMap.set(task.assignee.uid, task.assignee);
          }
        } else {
          showNoAssignee = true;
        }

        (task.relatedPersons || []).forEach((related: Assignee) => {
          if (!this.assigneeMap.has(related.uid)) {
            this.assigneeMap.set(related.uid, related);
          }
        });

        if (task.subprojectId !== null) {
          if (!this.paymentMap.has(task.subprojectId)) {
            this.paymentMap.set(task.subprojectId, {id: task.subprojectId, header: task.subprojectHeader});
          }
        } else {
          showNoPayment = true;
        }

        if (task.epicName !== null) {
          if (this.epicList.indexOf(task.epicName) < 0) {
            this.epicList.push(task.epicName);
          }
        } else {
          showNoEpic = true;
        }

        if (!!task.tag && this.tagList.indexOf(task.tag.header) < 0) {
          this.tagList.push(task.tag.header);
        }

        if (task.environmentList) {
          task.environmentList.filter(item => this.environmentList.indexOf(item) < 0).forEach(item => this.environmentList.push(item));
        }
      });
    });

    if (showNoAssignee) {
      this.assigneeMap.set('none', {name: this.emptyFliter, id: 0, uid: 'none', avatar: null});
    }

    this.epicList.sort(function(a, b) {
      return a.toLowerCase().localeCompare(b.toLowerCase());
    });
    this.tagList.sort(function(a, b) {
      return a.toLowerCase().localeCompare(b.toLowerCase());
    });
    this.environmentList.sort(function(a, b) {
      return a.toLowerCase().localeCompare(b.toLowerCase());
    });
    if (showNoEpic) {
      this.epicList.unshift(this.emptyFliter);
    }
    this.tagList.unshift(this.emptyFliter);
    this.environmentList.unshift(this.emptyFliter);

    if (this.selectedPayment && !this.paymentMap.has(this.selectedPayment.id) && this.selectedPayment.id != 0) {
      this.selectedPayment = null;
      this.router.navigate([], {queryParams: {payment: null}, queryParamsHandling: 'merge'});
    }

    if (this.selectedEpic && this.epicList.indexOf(this.selectedEpic) < 0) {
      this.selectedEpic = null;
      this.router.navigate([], {queryParams: {epic: null}, queryParamsHandling: 'merge'});
    }

    if (this.selectedTag && this.tagList.indexOf(this.selectedTag) < 0) {
      this.selectedTag = null;
      this.router.navigate([], {queryParams: {tag: null}, queryParamsHandling: 'merge'});
    }

    if (this.selectedEnvironment && this.environmentList.indexOf(this.selectedEnvironment) < 0) {
      this.selectedEnvironment = null;
      this.router.navigate([], {queryParams: {environment: null}, queryParamsHandling: 'merge'});
    }

    this.assigneeList = Array.from(this.assigneeMap.values());
    this.currentAssignee = this.assigneeList.filter(item => item.id === this.userId).shift();
    this.assigneeList.sort(function(a, b) {
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });

    this.paymentList = Array.from(this.paymentMap.values());
    if (showNoPayment) {
      this.paymentList.unshift({id: 0, header: this.emptyFliter});
      this.paymentMap.set(0, {id: 0, header: this.emptyFliter});
    }
    this.cdr.detectChanges();
  }

  getImageUri(picture: Picture): string {
    return this.fileService.getImageUri(picture, 96);
  }

  filterName(term: string) {
    term = term.trim();
    this.router.navigate([], {queryParams: {term: term ? term : null}, queryParamsHandling: 'merge'});
  }

  filterAssignee(assignee: Assignee) {
    if (!assignee || assignee.id != this.userId) {
      this.myAssignee = false;
    }

    if (assignee === null) {
      this.resetAssignee();
    } else if (assignee !== undefined) {
      this.selectedAssignee = assignee;
      this.router.navigate([], {queryParams: {assignee: assignee.uid}, queryParamsHandling: 'merge'});
    }
  }

  filterPayment(payment: IdAndHeader): void {
    if (payment) {
      this.router.navigate([], {queryParams: {payment: payment.id}, queryParamsHandling: 'merge'});
    } else {
      this.router.navigate([], {queryParams: {payment: null}, queryParamsHandling: 'merge'});
    }
  }

  filterDate(): void {
    const params = {from: null, to: null};
    if (this.dateRange?.from) {
      params.from = this.dateRange?.from.getFormattedDay('YMD', '-');
    }
    if (this.dateRange?.to) {
      params.to = this.dateRange?.to.getFormattedDay('YMD', '-');
    }

    this.router.navigate([], {queryParams: params, queryParamsHandling: 'merge'});
  }

  filterReleased(): void {
    const params = {rfrom: null, rto: null};
    if (this.releasedRange?.from) {
      params.rfrom = this.releasedRange?.from.getFormattedDay('YMD', '-');
    }
    if (this.releasedRange?.to) {
      params.rto = this.releasedRange?.to.getFormattedDay('YMD', '-');
    }

    this.router.navigate([], {queryParams: params, queryParamsHandling: 'merge'});
  }

  filterParams(param: string, value: string): void {
    const valueObject = {};
    if (value) {
      valueObject[param] = value;
    } else {
      valueObject[param] = null;
    }
    this.router.navigate([], {queryParams: valueObject, queryParamsHandling: 'merge'});
  }

  toggleMyAssignee(): void {
    this.myAssignee = !this.myAssignee;
    this.resetAssignee();

    if (this.myAssignee) {
      this.assigneeMap.forEach(assignee => {
        if (assignee.id == this.userId) {
          this.filterAssignee(assignee);
        }
      });

      // Пользователя нет в проекте
      if (!this.selectedAssignee) {
        this.filterAssignee({name: this.emptyFliter, id: this.userId, uid: this.userId.toString(), avatar: null});
      }
    }

    this.cdr.detectChanges();
  }

  resetAssignee(): void {
    this.selectedAssignee = null;
    this.router.navigate([], {queryParams: {assignee: null}, queryParamsHandling: 'merge'});
  }

  assigneeListFiltered() {
    return this.assigneeList.filter(item => {
      return item.id != this.userId;
    });
  }

  get dateRange(): TuiDayRange {
    return this.rangeForm.get('dateRange').value;
  }

  get releasedRange(): TuiDayRange {
    return this.releasedForm.get('releasedRange').value;
  }

  mineAssignee() {
    return this.assigneeList;
  }
}
